import type { DeploymentStage } from '@/constants/deployment'

/** Shared across both products — matches order-system CDK log group names. */
const SHARED_LOG_BLOCKS = [
  'dispatcher',
  'initiator',
  'order-system-api',
  'fetch-emails',
] as const

/** The order confirmation flow's blocks, including its fallbacks. */
const ORDER_CONFIRMATION_LOG_BLOCKS = [
  'connect-purchase-order',
  'extract-order-confirmation',
  'find-deviations',
  'identify-order-confirmations',
  'match-article-rows',
  'move-to-handled',
  'move-to-not-handled',
  'parse-order-confirmation',
  'save-order-confirmation-extraction',
  'set-order-confirmation-as-errored',
] as const

/**
 * The sales order flow's blocks, including its fallbacks. Kept in step by hand with the
 * workflow flow in order-system (buckets/<stage>/organizations/<org>/workflows/<id>/
 * configuration.json) — the frontend has no endpoint that reports which blocks a flow runs.
 */
const SALES_ORDER_LOG_BLOCKS = [
  'check-for-existing-sales-order',
  'evaluate-customer-order',
  'extract-customer-order',
  'identify-customer',
  'identify-customer-orders',
  'match-erp-articles',
  'match-erp-order-head',
  'move-to-not-handled',
  'parse-customer-order',
  'save-customer-order-extraction',
  'set-customer-order-as-errored',
  'set-customer-order-as-open',
] as const

/** Which product's workflow a process belongs to — they run different blocks. */
export type WorkflowProduct = 'order-confirmation' | 'sales-order'

const LOG_BLOCKS_BY_PRODUCT: Record<WorkflowProduct, readonly string[]> = {
  'order-confirmation': [...SHARED_LOG_BLOCKS, ...ORDER_CONFIRMATION_LOG_BLOCKS],
  'sales-order': [...SHARED_LOG_BLOCKS, ...SALES_ORDER_LOG_BLOCKS],
}

/** Matches order-system lambda log retention (RetentionDays_ONE_DAY). */
const RELATIVE_WINDOW_SECONDS = 86_400

/** CloudWatch console deep-link value encoding (editorString, etc.). */
function encodeCloudWatchDeepLinkValue(value: string): string {
  return value
    .replace(/\*/g, '*2a')
    .replace(/ /g, '*20')
    .replace(/@/g, '*40')
    .replace(/\n/g, '*0a')
    .replace(/\|/g, '*7c')
    .replace(/"/g, '*22')
    .replace(/,/g, '*2c')
    .replace(/=/g, '*3d')
    .replace(/:/g, '*3a')
}

function buildLogsInsightsQuery(processId: string): string {
  return [
    'fields @timestamp, @message',
    `| filter @message like /${processId}/`,
    '| sort @timestamp asc',
  ].join('\n')
}

function formatQueryDetail(editorString: string, logGroups: string[]): string {
  const encodedEditor = encodeCloudWatchDeepLinkValue(editorString)
  const sources = logGroups.map((group) => `~'${group}`).join('')

  // Segments are joined with '~'; only the first segment includes a leading '~'.
  return [
    '~(end~0',
    `start~-${RELATIVE_WINDOW_SECONDS}`,
    "timeType~'RELATIVE",
    "tz~'LOCAL",
    "unit~'seconds",
    `editorString~'${encodedEditor}`,
    "queryId~'00000000-0000-4000-8000-000000000001",
    `source~(${sources})`,
    "lang~'CWLI",
    "logClass~'STANDARD",
    "queryBy~'logGroupName)",
  ].join('~')
}

/**
 * Deep link into CloudWatch Logs Insights for a process ID across the log groups of the
 * blocks that product's workflow runs.
 * Searches the last 24 hours from when the link is opened (log retention limit).
 * Email received time is intentionally not used — processing may happen much later.
 * Requires AWS Console access in the target account.
 */
export function buildCloudWatchProcessLogsUrl(
  processId: string,
  stage: DeploymentStage,
  product: WorkflowProduct,
): string {
  const region = import.meta.env.VITE_AWS_REGION || 'eu-north-1'
  const editorString = buildLogsInsightsQuery(processId)
  const logGroups = LOG_BLOCKS_BY_PRODUCT[product].map((name) => `${name}-${stage}`)
  const queryDetail = formatQueryDetail(editorString, logGroups)

  const hash = `logsV2:logs-insights$3FqueryDetail$3D${queryDetail}`
  return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#${hash}`
}

export function canLinkProcessIdToCloudWatch(
  processId: string | undefined | null,
  stage: DeploymentStage | null,
  isFrontFlowStaff: boolean,
): processId is string {
  return isFrontFlowStaff && !!stage && !!processId
}
